import { AttachmentsZone } from '@kirz/mui-admin';
import {
  Grid,
  Typography,
  Box,
  Paper,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import React, { useContext } from 'react';

import { WebFormContext } from 'contexts/WebFormContext';

type FieldGroupProps = {
  title: string;
  children: React.ReactNode;
};

function FieldGroup({ title, children }: FieldGroupProps) {
  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
        {title}
      </Typography>
      <Grid container spacing={2}>
        {children}
      </Grid>
    </Box>
  );
}

function AttachmentField({
  label,
  value,
  mx = -2,
}: {
  label: string;
  value: any;
  mx?: number;
}) {
  return (
    <Box
      sx={{
        mt: 1,
        mx,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ ml: 2, mb: -3 }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          mt: -3,
          mx: -2,
          '& div': {
            border: 'thin solid transparent !important',
          },
          mb: -5,
        }}
      >
        <AttachmentsZone
          name=""
          entityId={{}}
          readOnly
          dropzoneProps={{}}
          grid={false}
          value={value ?? []}
          attachmentsTypes={[
            {
              value: 'other',
              text: 'Anders',
            },
          ]}
        />
      </Box>
    </Box>
  );
}

type FieldProps = {
  label: string;
  value: any;
  xs?: number;
  sm?: number;
};

function Field({ label, value, xs = 12, sm = 6 }: FieldProps) {
  if (!value && value !== 0) return null;

  return (
    <Grid item xs={xs} sm={sm}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography>{value}</Typography>
    </Grid>
  );
}

function formatDate(date: string) {
  if (!date) return '';
  return new Date(date).toLocaleDateString();
}

const deductionTypes = [
  {
    id: 'belgian_dividends',
    label: 'Belgische dividenden (vrijstelling eerste € 800)',
  },
  { id: 'french_dividends', label: 'Franse dividenden' },
  { id: 'other_foreign_dividends', label: 'Andere buitenlandse dividenden' },
  { id: 'other_income', label: 'Overige roerende inkomsten' },
  { id: 'life_insurance', label: 'Individuele levensverzekering' },
  { id: 'gifts', label: 'Giften' },
  { id: 'childcare', label: 'Kinderopvang' },
  { id: 'pension_savings', label: 'Pensioensparen' },
  { id: 'social_contributions', label: 'Sociale bijdragen (zelfstandigen)' },
  { id: 'vapz_pcz', label: 'VAPZ/PCZ' },
  { id: 'advance_payments', label: 'Voorafbetalingen' },
];

function DeductionsTable({ deductions }: { deductions: any }) {
  if (!deductions) return null;

  return (
    <TableContainer component={Paper} sx={{ mb: 3 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
            <TableCell sx={{ width: '60%' }}>Type</TableCell>
            <TableCell align="center" sx={{ width: '20%' }}>
              Aanvrager
            </TableCell>
            <TableCell align="center" sx={{ width: '20%' }}>
              Partner
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {deductionTypes.map((type) => (
            <TableRow
              key={type.id}
              sx={{
                '&:last-child td, &:last-child th': { border: 0 },
              }}
            >
              <TableCell component="th" scope="row" sx={{ py: 1 }}>
                {type.label}
              </TableCell>
              <TableCell align="center" sx={{ py: 1 }}>
                {deductions.applicant?.[type.id] ? '✅' : '❌'}
              </TableCell>
              <TableCell align="center" sx={{ py: 1 }}>
                {deductions.partner?.[type.id] ? '✅' : '❌'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

type OverviewProps = {
  form?: {
    data: any;
    name: string;
    isFilled: boolean;
    filledAt: string;
  };
  p?: number;
  screen?: boolean;
};

export default function Overview(props: OverviewProps) {
  const { form } = props?.form
    ? { form: props.form }
    : // eslint-disable-next-line react-hooks/rules-of-hooks
      useContext(WebFormContext);
  const data = form.data || {};

  return (
    <Box
      component="div"
      sx={{
        p: 0,
        ...((props.screen ?? true) && {
          maxWidth: 1200,
          mx: 'auto',
          height: '100vh',
          width: '100vw',
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#555',
          },
        }),
      }}
    >
      <Paper
        elevation={props.screen ? 3 : 0}
        sx={{
          py: props.p ?? { xs: 4, sm: 10 },
          px: props.p ?? { xs: 2, sm: 10 },
        }}
      >
        <Typography variant="h4" sx={{ mb: 4 }}>
          {form.name}
        </Typography>

        {/* Personal Information */}
        <FieldGroup title="Persoonlijke informatie">
          <Field label="Voornaam" value={data.voornaam} />
          <Field label="Naam" value={data.naam} />
          <Field label="E-mail" value={data.email} />
          <Field label="Rijksregisternummer" value={data.rijksregister} />
          <Field
            label="Identiteitskaart geldig tot"
            value={formatDate(data.id_kaart_geldig_tot)}
          />
          <AttachmentField
            label="Identiteitskaart"
            value={data.id_kaart_documenten}
          />
          <Field label="Burgerlijke staat" value={data.burgerlijke_staat} />
          <Field label="Huwelijksstelsel" value={data.huwelijksstelsel} />
          <Field label="Adres" value={data.adres} />
          <Field label="IBAN" value={data.iban} />
        </FieldGroup>

        {/* Partner Information 6 */}
        <FieldGroup title="Partner">
          <Field label="Voornaam partner" value={data.partner_voornaam} />
          <Field label="Naam partner" value={data.partner_naam} />
          <Field
            label="Rijksregisternummer partner"
            value={data.partner_rijksregister}
          />
          <Field
            label="Identiteitskaart geldig tot"
            value={formatDate(data.partner_id_kaart_geldig_tot)}
          />
          <AttachmentField
            label="Identiteitskaart partner"
            value={data.partner_id_kaart_documenten}
          />
        </FieldGroup>

        {/* Children 9 */}
        {data.children?.length > 0 && (
          <FieldGroup title="Kinderen ten laste">
            {data.children.map((child: any, index: number) => (
              <Grid item xs={12} key={index}>
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    Kind {index + 1}
                  </Typography>
                  <Grid container spacing={2}>
                    <Field label="Voornaam" value={child.voornaam} />
                    <Field label="Naam" value={child.naam} />
                    <Field
                      label="Geboortedatum"
                      value={formatDate(child.geboortedatum)}
                    />
                    <Field
                      label="Fiscaal co-ouderschap"
                      value={child.fiscaalCoOuderschap}
                    />
                  </Grid>
                </Paper>
              </Grid>
            ))}
          </FieldGroup>
        )}

        {/* Other Dependents 10 */}
        {data.dependents?.length > 0 && (
          <FieldGroup title="Andere personen ten laste">
            {data.dependents.map((dependent: any, index: number) => (
              <Grid item xs={12} key={index}>
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    Persoon {index + 1}
                  </Typography>
                  <Grid container spacing={2}>
                    <Field label="Voornaam" value={dependent.voornaam} />
                    <Field label="Naam" value={dependent.naam} />
                    <Field
                      label="Verwantschap"
                      value={dependent.verwantschap}
                    />
                    <Field
                      label="Geboortedatum"
                      value={formatDate(dependent.geboortedatum)}
                    />
                    <Field
                      label="Zorgbehoevend"
                      value={dependent.zorgbehoevend}
                    />
                  </Grid>
                </Paper>
              </Grid>
            ))}
          </FieldGroup>
        )}

        {/* Houses 12 */}
        {data.houses?.length > 0 && (
          <FieldGroup title="Woningen in België">
            {data.houses.map((house: any, index: number) => (
              <Grid item xs={12} key={index}>
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    Woning {index + 1}
                  </Typography>
                  <Grid container spacing={2}>
                    <Field label="Adres" value={house.adres} />
                    <Field label="Bestemming" value={house.bestemming} />
                    <Field label="Kadastraal inkomen" value={house.ki} />
                    {house.bestemming?.includes('Verhuurd') && (
                      <>
                        <Field
                          label="Verhuurd van"
                          value={formatDate(house.verhuurdVanDatum)}
                        />
                        <Field
                          label="Verhuurd tot"
                          value={formatDate(house.verhuurdTotDatum)}
                        />
                        <Field
                          label="Ontvangen bruto huur"
                          value={house.ontvangenBrutoHuur}
                        />
                      </>
                    )}
                    <AttachmentField
                      label="Documenten"
                      value={house.documenten}
                      mx={0}
                    />
                  </Grid>
                </Paper>
              </Grid>
            ))}
          </FieldGroup>
        )}

        {/* Foreign Houses 13*/}
        {data.foreign_houses?.length > 0 && (
          <FieldGroup title="Woningen in Buitenland">
            {data.foreign_houses.map((house: any, index: number) => (
              <Grid item xs={12} key={index}>
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    Woning {index + 1}
                  </Typography>
                  <Grid container spacing={2}>
                    <Field label="Adres" value={house.adres} />
                    <Field label="Bestemming" value={house.bestemming} />
                    <Field label="Kadastraal inkomen" value={house.ki} />
                    {house.bestemming?.includes('Verhuurd') && (
                      <>
                        <Field
                          label="Verhuurd van"
                          value={formatDate(house.verhuurdVanDatum)}
                        />
                        <Field
                          label="Verhuurd tot"
                          value={formatDate(house.verhuurdTotDatum)}
                        />
                        <Field
                          label="Ontvangen bruto huur"
                          value={house.ontvangenBrutoHuur}
                        />
                      </>
                    )}
                    <AttachmentField
                      label="Documenten"
                      value={house.documenten}
                      mx={0}
                    />
                  </Grid>
                </Paper>
              </Grid>
            ))}
          </FieldGroup>
        )}

        {/* Received Payments 14 */}
        {data.received_payments?.length > 0 && (
          <FieldGroup title="Ontvangen onderhoudsuitkeringen">
            {data.received_payments.map((payment: any, index: number) => (
              <Grid item xs={12} key={index}>
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    Uitkering {index + 1}
                  </Typography>
                  <Grid container spacing={2}>
                    <Field label="Voornaam" value={payment.voornaam} />
                    <Field label="Naam" value={payment.naam} />
                    <Field label="Bedrag" value={payment.bedrag} />
                    <AttachmentField
                      label="Documenten"
                      value={payment.documenten}
                      mx={0}
                    />
                  </Grid>
                </Paper>
              </Grid>
            ))}
          </FieldGroup>
        )}

        {/* Paid Payments 15 */}
        {data.paid_payments?.length > 0 && (
          <FieldGroup title="Betaalde onderhoudsuitkeringen">
            {data.paid_payments.map((payment: any, index: number) => (
              <Grid item xs={12} key={index}>
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    Uitkering {index + 1}
                  </Typography>
                  <Grid container spacing={2}>
                    <Field label="Voornaam" value={payment.voornaam} />
                    <Field label="Naam" value={payment.naam} />
                    <Field label="Bedrag" value={payment.bedrag} />
                    <AttachmentField
                      label="Documenten"
                      value={payment.documenten}
                      mx={0}
                    />
                  </Grid>
                </Paper>
              </Grid>
            ))}
          </FieldGroup>
        )}

        {/* Deductions 16 */}
        <FieldGroup title="Verminderingen">
          <Grid item xs={12}>
            <DeductionsTable deductions={data.deductions} />
            <AttachmentField
              label="Verminderingen documenten"
              value={data.deductions_documents}
            />
          </Grid>
        </FieldGroup>

        {/* Other Information 17 */}
        <FieldGroup title="Overige informatie">
          <Field
            label="Buitenlandse bankrekeningen"
            value={data.has_foreign_bank_accounts}
          />
          {data.has_foreign_bank_accounts === 'ja' && (
            <Field
              label="Melding bij CAP (NBB)"
              value={data.report_foreign_accounts_to_cap}
            />
          )}
          <Field
            label="Buitenlandse individuele levensverzekeringen"
            value={data.has_life_insurance}
          />
          <Field label="Juridische structuren" value={data.legal_structures} />
          <Field
            label="Effectenrekeningen"
            value={
              data.securities_accounts === 'ja_meer_dan_1'
                ? 'Ja - meer dan 1'
                : 'Neen - slechts 1 of geen effectenrekening'
            }
          />
          <Field
            label="Lening via crowdfundingplatform en/of Winwinleningen"
            value={data.crowdfunding_winwin_lening}
          />
          <Field
            label="Vennootschap betaalt huur aan u als privé persoon"
            value={data.vennootschap_huur_prive}
          />
          <Field
            label="Voordeel alle aard (titularis)"
            value={data.voordeel_alle_aard_titularis}
          />
          <Field
            label="Voordeel alle aard (partner)"
            value={data.voordeel_alle_aard_partner}
          />
          <Field label="Bevestigingstekst" value={data.confirmation_text} />
        </FieldGroup>

        {/* Documents Section 21 */}
        <FieldGroup title="Documenten">
          <Grid item xs={12}>
            <AttachmentField
              label="Buitenlandse beroepsinkomsten"
              value={data.foreign_income_documents}
            />
            <AttachmentField
              label="Buitenlandse pensioenen"
              value={data.foreign_pension_documents}
            />
            <AttachmentField
              label="Vervangingsinkomsten"
              value={data.replacement_income_documents}
            />
            <AttachmentField
              label="Vervangingsuitkering bijdrage"
              value={data.replacement_payment_documents}
            />
            <AttachmentField
              label="Afkopen studiejaren"
              value={data.study_years_documents}
            />
          </Grid>
        </FieldGroup>

        {/* Form Status */}
        <Divider sx={{ my: 4 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            Status: {form.isFilled ? 'Ingevuld' : 'In behandeling'}
          </Typography>
          {form.isFilled && (
            <Typography variant="body2" color="text.secondary">
              Ingevuld op: {formatDate(form.filledAt!)}
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
